/**
 * Builds the per-block inline-edit index for the bridge init payload
 * (spec §5.6). The PHP contract (Base::inlineNormalized) declares mode per
 * attribute; legacy string|null selector values are still accepted and fall
 * back to the historical client-side derivation.
 */

import type { InitPayload, InlineMode } from './protocol';
import type { BlockNode } from './types';
import { isSharedBlock, markerId } from './types';
import type { TypeInfo } from './useBuilderRegistry';

const MODES: InlineMode[] = ['plain', 'headline', 'richtext'];

const MODE_BY_TYPE: Record<string, Record<string, InlineMode>> = {
    headline: { content: 'headline' },
};

type InlineValue = string | { selector: string | null; mode: string } | null;

function normalizeAttr(slug: string, attr: string, value: InlineValue): { selector: string | null; mode: InlineMode } {
    if (value !== null && typeof value === 'object') {
        const mode = MODES.includes(value.mode as InlineMode) ? (value.mode as InlineMode) : 'plain';

        return { selector: value.selector ?? null, mode };
    }

    return { selector: value, mode: MODE_BY_TYPE[slug]?.[attr] ?? 'plain' };
}

export function buildInlineIndex(content: BlockNode[], types: TypeInfo[]): NonNullable<InitPayload['inline']> {
    const byType = new Map(types.map((type) => [type.slug, type]));
    const index: NonNullable<InitPayload['inline']> = {};

    const visit = (blocks: BlockNode[]): void => {
        for (const block of blocks) {
            const id = markerId(block);
            const type = isSharedBlock(block) ? undefined : byType.get(String(block.type));

            if (id !== null && type?.inline) {
                const attrs: Record<string, { selector: string | null; mode: InlineMode }> = {};

                for (const [attr, value] of Object.entries(type.inline)) {
                    attrs[attr] = normalizeAttr(type.slug, attr, value);
                }

                index[id] = attrs;
            }

            if (block.children?.length) {
                visit(block.children);
            }
        }
    };

    visit(content);

    return index;
}
